/**
 * Agent tools over the site-configuration store.
 *
 * Schema convention matches services/tools.ts and cms/tools.ts: required
 * fields are bare, everything optional is `.nullable()` with a `.describe()`
 * saying what null means. Never `.optional()` or `.default()` — the
 * provider's strict function schema requires every property in `required`,
 * and `.optional()` marks a field as not-required, which makes the whole
 * request 400.
 *
 * All mutations go through siteconfig/store.ts, i.e. localStorage only.
 */

import { tool } from "ai"
import { z } from "zod"
import { ulid } from "ulid"
import { withQueue } from "@/pages/dev/smart-cms/shared/toolQueue"
import * as db from "@/pages/dev/smart-cms/site-config/store"
import {
  type SiteConfigStore, type SiteConfigEntityKind, type PageContentType,
  SITECONFIG_BUCKET, siteConfigDisplayName, PAGECONTENT_FIELD_GROUPS, PAGECONTENT_TYPE_LABELS,
  SERVICES_COMPONENT_FLAGS, SERVICES_DETAIL_FLAGS,
} from "@/pages/dev/smart-cms/site-config/types"
import type { ChangeAction, ChangeEntry, EntityVersion } from "@/pages/dev/smart-cms/site-config/history"

export interface SiteConfigToolContext {
  getStore: () => SiteConfigStore
  setStore: (next: SiteConfigStore) => void
  locale: string
  onEntityFocused?: (kind: SiteConfigEntityKind, id: string) => void
  onEntityDeleted?: (kind: SiteConfigEntityKind, id: string) => void
  onChanged?: () => void
  /** HITL gate for destructive operations. */
  onRequestApproval?: (req: { summary: string; detail?: string }) => Promise<boolean>
  autoAccept?: boolean
  /** Records a change so it appears in the audit log and can be reverted. */
  record?: (args: {
    prevStore: SiteConfigStore
    nextStore?: SiteConfigStore
    action: ChangeAction
    actor: "agent" | "user"
    entityKind: SiteConfigEntityKind
    entityId: string
    summary: string
    locale?: string
  }) => void
  getHistory?: () => ChangeEntry[]
  revert?: (entryId: string) => { ok: true; description: string } | { ok: false; error: string }
  versionsOf?: (entityId: string) => EntityVersion[]
  restoreEntityVersion?: (entityId: string, entryId: string, kind: SiteConfigEntityKind) =>
    { ok: true; description: string } | { ok: false; error: string }
}

const ok = (data: any) => data
const fail = (message: string) => ({ error: message })

function tryDo<T>(fn: () => T): T | { error: string } {
  try { return fn() } catch (e: any) { return fail(e?.message || String(e)) }
}

const ENTITY_KINDS = Object.keys(SITECONFIG_BUCKET) as [SiteConfigEntityKind, ...SiteConfigEntityKind[]]

/**
 * Fields that only exist on a Site. The store is untyped at runtime, so
 * without this a stray `locales` or `baseUrl` aimed at a pageContent block
 * would persist silently and be invisible to every editor that reads it.
 * Rejected loudly instead — a wrong-kind write is a bug, not a no-op.
 */
const SITE_ONLY_FIELDS = new Set([
  "name", "baseUrl", "logo", "defaultLocale", "locales", "googleTag", "googleTagJson",
  "transifexProject", "googleTranslate", "cookieBanner",
  "cookieBannerTranslations", "serviceMap", "articles",
  "headerMenu", "footer", "contentIds",
])
const PAGE_CONTENT_TYPES = Object.keys(PAGECONTENT_TYPE_LABELS) as [PageContentType, ...PageContentType[]]

/** Drops the nulls, which mean "not supplied" rather than "set to null". */
function usedFields(fields: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(fields)) if (v !== null && v !== undefined) out[k] = v
  return out
}

/**
 * components/componentsDetails are real JSON objects in the store, but the
 * tool schema accepts them as JSON strings (object schemas with unknown keys
 * aren't representable in strict function-calling) — parse them back before
 * writing. Throws with a clear message on invalid JSON rather than silently
 * dropping the field.
 */
function parseJsonObjectFields(
  fields: Record<string, any>,
  /**
   * The block as currently STORED. Required for a merge to be correct on an
   * update: updateEntity does an Object.assign, so a components object built
   * only from this call's flags would replace the stored one and clear every
   * flag the caller did not mention. Omit only when creating, where there is
   * nothing to preserve.
   */
  existing?: Record<string, any>,
): Record<string, any> {
  const out = { ...fields }

  // The tool schema exposes the services-block flags FLAT (hidemap, hidelist,
  // …) because a nested object with fixed keys is clumsy to fill and an open
  // object is not representable in strict function-calling at all. The store
  // shape is two nested objects, so fold them back here.
  const fold = (target: "components" | "componentsDetails", keys: readonly string[]) => {
    // `false` is a real value here — every flag but showIA is a HIDE flag, so
    // testing truthiness would make "unhide this" impossible to express.
    const present = keys.filter(k => out[k] !== undefined && out[k] !== null)
    for (const k of keys) delete out[k]
    if (!present.length) return
    out[target] = { ...(existing?.[target] ?? {}), ...(out[target] ?? {}) }
    for (const k of present) out[target][k] = fields[k]
  }
  fold("components", SERVICES_COMPONENT_FLAGS)
  fold("componentsDetails", SERVICES_DETAIL_FLAGS)

  return out
}

/**
 * z.record() produces a JSON Schema object with no `properties`, which is
 * invalid for OpenAI's strict function-calling (the provider rejects the
 * WHOLE tool list with "required key supplied" once it can't reconcile that
 * object's required array). Locale maps (headerMenu item titles, footer
 * text/link titles) are passed as a JSON string instead and parsed here.
 */
function parseLocaleMap(json: string, fieldName: string): Record<string, string> {
  let parsed: unknown
  try { parsed = JSON.parse(json) } catch { throw new Error(`"${fieldName}" must be a JSON object string, e.g. {"en-US":"About"} — got: ${json}`) }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error(`"${fieldName}" must be a JSON object of locale -> text, e.g. {"en-US":"About"}`)
  }
  return parsed as Record<string, string>
}

/**
 * The PageContent fields an agent can write directly. Covers every field the
 * editor exposes (see SiteConfigEditor.tsx's FIELD_META) plus the id arrays;
 * deliberately not all ~90 raw Directus columns, since most of that size is
 * near-duplicate social-channel fields already covered by the ones listed.
 */
const PAGE_CONTENT_FIELDS = {
  title: z.string().nullable().describe("Admin-facing name identifying this block in the content list. Not shown on the public page, not translated."),
  aboutTitle: z.string().nullable().describe("About block title"),
  aboutSubtitle: z.string().nullable().describe("About block HTML body (Directus field is named 'subtitle' but holds the rich body text)"),
  aboutImage: z.string().nullable().describe("Image URL. You cannot upload files — if the user wants a new image, tell them to use the Upload button on this field in the editor, then reference the URL it produces. Never invent a file id."),
  statementTitle: z.string().nullable().describe("Mission statement text"),
  missionImage: z.string().nullable().describe("Image URL. You cannot upload files — if the user wants a new image, tell them to use the Upload button on this field in the editor, then reference the URL it produces. Never invent a file id."),
  channelsTitle: z.string().nullable().describe("Channels block title"),
  channelsSubtitle: z.string().nullable().describe("Channels block subtitle"),
  fb: z.string().nullable(), fbLink: z.string().nullable(),
  fbmess: z.string().nullable(), fbmessLink: z.string().nullable(),
  instagram: z.string().nullable(), instagramLink: z.string().nullable(),
  telegram: z.string().nullable(), telegramLink: z.string().nullable(),
  whatsapp: z.string().nullable(), whatsappLink: z.string().nullable(),
  whatsappc: z.string().nullable(), whatsappcLink: z.string().nullable(),
  email: z.string().nullable(), emailLink: z.string().nullable(),
  facetime: z.string().nullable(), facetimeLink: z.string().nullable(),
  toktok: z.string().nullable(), tiktokLink: z.string().nullable(),
  x: z.string().nullable(), xLink: z.string().nullable(),
  youtube: z.string().nullable(), youtubeLink: z.string().nullable(),
  infoTitle: z.string().nullable(),
  infoSubtitle: z.string().nullable(),
  servicesTitle: z.string().nullable(),
  servicesSubtitle: z.string().nullable(),
  // Real booleans rather than a JSON string: the flag set is known, so the
  // schema can enforce it. NOTE the polarity — every flag except showIA HIDES
  // that piece, so false/absent means visible.
  hidemap: z.boolean().nullable().describe("services block: hide the map"),
  hidelist: z.boolean().nullable().describe("services block: hide the list"),
  hidesearch: z.boolean().nullable().describe("services block: hide the search box"),
  hideservicetype: z.boolean().nullable().describe("services block: hide the service-type filter"),
  hideaccesibility: z.boolean().nullable().describe("services block: hide the accessibility filter (Directus spells it 'accesibility')"),
  hidepopulation: z.boolean().nullable().describe("services block: hide the populations filter"),
  hideproviders: z.boolean().nullable().describe("services block: hide the providers filter"),
  showIA: z.boolean().nullable().describe("services block: show the IA entry point (the one flag that is NOT a hide flag)"),
  hidelastupdate: z.boolean().nullable().describe("services block: hide the last-updated stamp"),
  textAccessibility: z.string().nullable(),
  textPopulations: z.string().nullable(),
  textProviders: z.string().nullable(),
  textTypeOfService: z.string().nullable(),
  richtextTitle: z.string().nullable(),
  richtext: z.string().nullable().describe("HTML body"),
  categoriesTitle: z.string().nullable(),
  categoriesSubtitle: z.string().nullable(),
  sectionsTitle: z.string().nullable(),
  sectionsSubtitle: z.string().nullable(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactSubject: z.string().nullable(),
  contactMessage: z.string().nullable(),
  popupTitle: z.string().nullable(),
  popupText: z.string().nullable(),
  popupSubtitle: z.string().nullable(),
  buttonText: z.string().nullable().describe("Popup CTA button label"),
  buttonLink: z.string().nullable().describe("Popup CTA button URL"),
  text: z.string().nullable().describe("Generic block text"),
  textBold: z.boolean().nullable(),
  textSize: z.number().nullable(),
  textColor: z.string().nullable().describe("Hex color, e.g. #000000"),
  fontWeight: z.number().nullable(),
  bgColor: z.string().nullable(), colorBg: z.string().nullable(), colorText: z.string().nullable(),
  image: z.string().nullable().describe("Image URL. You cannot upload files — if the user wants a new image, tell them to use the Upload button on this field in the editor, then reference the URL it produces. Never invent a file id."),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
}

/**
 * The subset of PAGE_CONTENT_FIELDS that actually has a slot on
 * TranslatedPageContent (siteconfig/types.ts). Everything else — colours,
 * images, coordinates, booleans, numbers, and every *Link URL — is
 * locale-independent in Directus and must not be written into a translation
 * row, where it would be invisible to the editor and silently dropped on any
 * round trip through the typed shape.
 *
 * Keep this list in sync with TranslatedPageContent.
 */
const TRANSLATABLE_FIELD_KEYS = [
  "text", "richtext", "richtextTitle",
  "channelsTitle", "channelsSubtitle",
  "email", "facetime", "fb", "fbmess", "instagram", "telegram", "toktok",
  "whatsapp", "whatsappc", "x", "youtube",
  "servicesTitle", "servicesSubtitle",
  "textAccessibility", "textPopulations", "textProviders", "textTypeOfService",
  "infoTitle", "infoSubtitle",
  "categoriesTitle", "categoriesSubtitle",
  "sectionsTitle", "sectionsSubtitle",
  "statementTitle",
  "aboutTitle", "aboutSubtitle",
  "contactName", "contactEmail", "contactSubject", "contactMessage",
  "popupTitle", "popupText", "popupSubtitle", "buttonText", "buttonLink",
  "image", "aboutImage", "missionImage",
] as const satisfies readonly (keyof typeof PAGE_CONTENT_FIELDS)[]

const TRANSLATABLE_PAGE_CONTENT_FIELDS = Object.fromEntries(
  TRANSLATABLE_FIELD_KEYS.map(k => [k, PAGE_CONTENT_FIELDS[k]])
) as Pick<typeof PAGE_CONTENT_FIELDS, (typeof TRANSLATABLE_FIELD_KEYS)[number]>

export function makeSiteConfigTools(ctx: SiteConfigToolContext, hitlQueue?: { current: Promise<void> }) {
  const L = () => ctx.locale
  const commit = (next: SiteConfigStore) => { ctx.setStore(next); ctx.onChanged?.() }

  const commitTracked = (args: {
    prevStore: SiteConfigStore; nextStore: SiteConfigStore; action: ChangeAction
    entityKind: SiteConfigEntityKind; entityId: string; summary: string; locale?: string
  }) => {
    commit(args.nextStore)
    ctx.record?.({
      prevStore: args.prevStore, nextStore: args.nextStore, action: args.action,
      actor: "agent", entityKind: args.entityKind, entityId: args.entityId,
      summary: args.summary, locale: args.locale,
    })
  }

  const commitTrackedDelete = (args: {
    prevStore: SiteConfigStore; nextStore: SiteConfigStore
    entityKind: SiteConfigEntityKind; entityId: string; summary: string
  }) => {
    ctx.record?.({
      prevStore: args.prevStore, action: "delete", actor: "agent",
      entityKind: args.entityKind, entityId: args.entityId, summary: args.summary,
    })
    commit(args.nextStore)
  }

  const gate = async (summary: string, detail?: string): Promise<boolean> => {
    if (ctx.autoAccept) return true
    if (!ctx.onRequestApproval) return true
    const ask = () => ctx.onRequestApproval!({ summary, detail })
    // Serialize approvals so concurrent destructive calls in one model step do
    // not clobber each other's prompt. withQueue (shared with the CMS backend)
    // also catches a rejected approval and rethrows it here — an inline
    // try/finally would leave the rejection on hitlQueue.current, poisoning the
    // shared chain for every later approval in the session.
    return hitlQueue ? withQueue(hitlQueue, ask) : ask()
  }

  const name = (kind: SiteConfigEntityKind, id: string) => db.displayName(ctx.getStore(), kind, id, L())

  return {
    // ── Read ───────────────────────────────────────────────────────────────
    siteconfig_overview: tool({
      description: "Overview of the site-configuration database: entity counts, and how many page-content blocks exist per type. Call this first.",
      parameters: z.object({}),
      execute: async () => tryDo(() => ok(db.stats(ctx.getStore()))),
    }),

    siteconfig_search: tool({
      description: "Ranked keyword search across site configs, header/footer links, and page-content blocks (including translated text).",
      parameters: z.object({
        query: z.string(),
        kinds: z.array(z.enum(ENTITY_KINDS)).nullable().describe("Restrict to these entity kinds, or null for all"),
        limit: z.number().nullable(),
      }),
      execute: async ({ query, kinds, limit }) => tryDo(() => {
        const results = db.searchStore(ctx.getStore(), query, { kinds: kinds ?? undefined, limit: limit ?? 25 })
        return ok({ query, resultCount: results.length, results })
      }),
    }),

    siteconfig_list: tool({
      description: "List every entity of one kind, with ids and names.",
      parameters: z.object({
        kind: z.enum(ENTITY_KINDS),
        limit: z.number().nullable(),
      }),
      execute: async ({ kind, limit }) => tryDo(() => {
        const s = ctx.getStore()
        const rows = db.listEntities(s, kind)
        return ok({
          kind, total: rows.length,
          items: rows.slice(0, limit ?? 200).map((r: any) => ({
            id: r.id, name: siteConfigDisplayName(kind, r, L(), s.defaultLocale),
            locales: Object.keys(r.translations ?? {}),
            ...(kind === "pageContent" ? { type: r.type } : {}),
            ...(kind === "site" ? { serviceMap: r.serviceMap, articles: r.articles } : {}),
          })),
        })
      }),
    }),

    siteconfig_get: tool({
      description: "Read one entity in full, with every field and all locales. Use before updating so you know the current values.",
      parameters: z.object({ kind: z.enum(ENTITY_KINDS), id: z.string() }),
      execute: async ({ kind, id }) => tryDo(() => {
        const s = ctx.getStore()
        const e = db.getEntity(s, kind, id)
        if (!e) return fail(`No such ${kind}: ${id}`)
        ctx.onEntityFocused?.(kind, id)

        const resolved: Record<string, any> = {}
        if (kind === "pageContent") {
          resolved.canonicalFields = PAGECONTENT_FIELD_GROUPS[e.type as PageContentType] ?? []
          resolved.typeLabel = PAGECONTENT_TYPE_LABELS[e.type as PageContentType] ?? e.type
        }
        if (kind === "site") {
          // Ordered: this array's order is the on-page render order.
          resolved.pageContentIdsInPageOrder = e.contentIds
          resolved.pageContentOrderNote =
            "contentIds order is the top-to-bottom order these blocks render in. Change it with siteconfig_reorder_content."
          resolved.headerMenuItemCount = db.flattenMenu(e.headerMenu ?? []).length
          resolved.footerLinkCount = e.footer?.footerLinks?.length ?? 0
        }

        return ok({
          kind, entity: e, resolved,
          updatedAt: e.updatedAt ? new Date(e.updatedAt).toISOString() : null,
          createdAt: e.createdAt ? new Date(e.createdAt).toISOString() : null,
        })
      }),
    }),

    siteconfig_pagecontent_field_guide: tool({
      description: "The canonical fields for each of the 12 page-content types, so you know which fields a block of a given type is meant to use. Call this before creating or editing a pageContent block.",
      parameters: z.object({}),
      execute: async () => ok({
        types: PAGE_CONTENT_TYPES.map(t => ({
          type: t, label: PAGECONTENT_TYPE_LABELS[t], canonicalFields: PAGECONTENT_FIELD_GROUPS[t],
        })),
        note:
          "Production data does not strictly respect these groupings — some rows have fields set outside their own " +
          "type's group, left over from earlier edits. Prefer writing only the canonical fields for a block's type " +
          "unless the user specifically asks to fix or repurpose stray data on a row.",
      }),
    }),

    // ── Create ───────────────────────────────────────────────────────────────
    siteconfig_create_site: tool({
      description:
        "Create a Site — the top-level entity. A Site owns its own identity, locales, analytics and branding, " +
        "and REFERENCES geography from the Service Map backend and categories from the Articles backend. " +
        "Several sites may scope the same country; there is no exclusivity. " +
        "After creating, use siteconfig_set_service_map_scope and siteconfig_set_articles_scope to wire up associations.",
      parameters: z.object({
        name: z.string().nullable().describe("Public-facing site name, e.g. \"Signpost Greece\""),
        baseUrl: z.string().nullable().describe("Public site base url"),
        defaultLocale: z.string().nullable().describe("Fallback locale, e.g. \"en-US\""),
        locales: z.array(z.string()).nullable().describe("Every locale this site publishes in"),
        cookieBanner: z.string().nullable(),
      }),
      execute: async ({ name, baseUrl, defaultLocale, locales, cookieBanner }) => tryDo(() => {
        const s0 = ctx.getStore()
        const fields = usedFields({ name, baseUrl, defaultLocale, locales, cookieBanner })
        const { store, id } = db.createEntity(s0, "site", fields)
        commitTracked({ prevStore: s0, nextStore: store, action: "create", entityKind: "site", entityId: id, summary: `Created site ${name || "(untitled)"}` })
        ctx.onEntityFocused?.("site", id)
        return ok({ id })
      }),
    }),

    siteconfig_set_service_map_scope: tool({
      description:
        "Set which Service Map geography a Site surfaces. The three lists are independent and free-form: " +
        "listing a city does NOT require listing its country, so a city-level site is expressed by cityIds alone. " +
        "An empty scope means the site surfaces NO services. These are FKs into the Service Map backend, which " +
        "owns the countries, regions and cities themselves — this tool only references them.",
      parameters: z.object({
        siteId: z.string(),
        countryIds: z.array(z.string()).nullable().describe("Replaces the whole list; null leaves it unchanged"),
        regionIds: z.array(z.string()).nullable(),
        cityIds: z.array(z.string()).nullable(),
      }),
      execute: async ({ siteId, countryIds, regionIds, cityIds }) => tryDo(() => {
        const s0 = ctx.getStore()
        const site = s0.sites[siteId]
        if (!site) throw new Error(`No such site: ${siteId}`)
        const prev = site.serviceMap ?? { countryIds: [], regionIds: [], cityIds: [] }
        const serviceMap = {
          countryIds: countryIds ?? prev.countryIds ?? [],
          regionIds: regionIds ?? prev.regionIds ?? [],
          cityIds: cityIds ?? prev.cityIds ?? [],
        }
        const store = db.updateEntity(s0, "site", siteId, { serviceMap })
        const n = serviceMap.countryIds.length + serviceMap.regionIds.length + serviceMap.cityIds.length
        commitTracked({ prevStore: s0, nextStore: store, action: "update", entityKind: "site", entityId: siteId, summary: `Scoped ${db.displayName(s0, "site", siteId, L())} to ${n} geographic area(s)` })
        return ok({ serviceMap })
      }),
    }),

    siteconfig_set_articles_scope: tool({
      description:
        "Set which article categories a Site surfaces. allCategories=true publishes the whole knowledge base and " +
        "makes categoryIds irrelevant — prefer it unless the user wants a specific subset, because it keeps the " +
        "site from going stale when a new category is added. categoryIds are FKs into the Articles backend.",
      parameters: z.object({
        siteId: z.string(),
        allCategories: z.boolean().nullable(),
        categoryIds: z.array(z.string()).nullable().describe("Replaces the whole list; null leaves it unchanged"),
      }),
      execute: async ({ siteId, allCategories, categoryIds }) => tryDo(() => {
        const s0 = ctx.getStore()
        const site = s0.sites[siteId]
        if (!site) throw new Error(`No such site: ${siteId}`)
        const prev = site.articles ?? { allCategories: true, categoryIds: [] }
        const articles = {
          allCategories: allCategories ?? prev.allCategories ?? true,
          categoryIds: categoryIds ?? prev.categoryIds ?? [],
        }
        const store = db.updateEntity(s0, "site", siteId, { articles })
        commitTracked({ prevStore: s0, nextStore: store, action: "update", entityKind: "site", entityId: siteId, summary: articles.allCategories ? `Set ${db.displayName(s0, "site", siteId, L())} to publish all article categories` : `Scoped ${db.displayName(s0, "site", siteId, L())} to ${articles.categoryIds.length} article category(ies)` })
        return ok({ articles })
      }),
    }),

    siteconfig_create_pagecontent: tool({
      description: "Create a page-content block of a given type. Call siteconfig_pagecontent_field_guide first to know which fields to fill for that type. Not automatically attached to a site config — use siteconfig_attach_pagecontent.",
      parameters: z.object({
        type: z.enum(PAGE_CONTENT_TYPES),
        fields: z.object(PAGE_CONTENT_FIELDS).describe("Field values. Send null for anything not applicable to this type."),
      }),
      execute: async ({ type, fields: rawFields }) => tryDo(() => {
        const fields = parseJsonObjectFields(usedFields(rawFields))
        const s0 = ctx.getStore()
        const { store, id } = db.createEntity(s0, "pageContent", { type, ...fields })
        commitTracked({ prevStore: s0, nextStore: store, action: "create", entityKind: "pageContent", entityId: id, summary: `Created ${type} page-content block` })
        ctx.onEntityFocused?.("pageContent", id)
        return ok({ id, type })
      }),
    }),

    siteconfig_attach_pagecontent: tool({
      description: "Attaches a page-content block to a site config (appends to contentIds). Use to make a newly created block actually show up on a country's site.",
      parameters: z.object({
        siteId: z.string(),
        pageContentId: z.string(),
      }),
      execute: async ({ siteId, pageContentId }) => tryDo(() => {
        const s0 = ctx.getStore()
        const sc = s0.sites[siteId]
        if (!sc) return fail(`No such site: ${siteId}`)
        if (!db.getEntity(s0, "pageContent", pageContentId)) return fail(`No such pageContent: ${pageContentId}`)
        if (sc.contentIds.includes(pageContentId)) return ok({ siteId, note: "Already attached; nothing to do." })

        const after = db.updateEntity(s0, "site", siteId, { contentIds: [...sc.contentIds, pageContentId] })
        commitTracked({ prevStore: s0, nextStore: after, action: "update", entityKind: "site", entityId: siteId, summary: "Attached page-content block" })
        return ok({ siteId, attached: pageContentId })
      }),
    }),

    siteconfig_reorder_content: tool({
      description:
        "Sets the order page-content blocks render in on a site config. contentIds order IS the on-page order, top to bottom. " +
        "Pass the complete list of block ids in the order you want; every id currently attached must appear exactly once. " +
        "Read the current order from siteconfig_get first.",
      parameters: z.object({
        siteId: z.string(),
        orderedPageContentIds: z.array(z.string())
          .describe("Every attached block id, in the desired top-to-bottom order"),
      }),
      execute: async ({ siteId, orderedPageContentIds }) => tryDo(() => {
        const before = ctx.getStore()
        const sc = before.sites[siteId]
        if (!sc) return fail(`No such site: ${siteId}`)

        // A reorder must be a pure PERMUTATION. Accepting a partial or padded
        // list would silently detach or duplicate blocks, which looks like a
        // reorder until someone notices content missing from the page.
        const current = [...(sc.contentIds ?? [])]
        const next = [...orderedPageContentIds]
        const asSorted = (xs: string[]) => [...xs].sort().join("\u0000")
        if (next.length !== current.length || asSorted(next) !== asSorted(current)) {
          const missing = current.filter(id => !next.includes(id))
          const unknown = next.filter(id => !current.includes(id))
          const dupes = next.filter((id, i) => next.indexOf(id) !== i)
          return fail(
            "The list must be a reordering of exactly the blocks already attached. " +
            [
              missing.length ? `Missing: ${missing.join(", ")}` : "",
              unknown.length ? `Not attached to this config: ${unknown.join(", ")}` : "",
              dupes.length ? `Listed more than once: ${[...new Set(dupes)].join(", ")}` : "",
            ].filter(Boolean).join(". ") +
            ". To add or remove a block use siteconfig_attach_pagecontent or siteconfig_delete."
          )
        }

        if (next.every((id, i) => id === current[i])) {
          return ok({ siteId, note: "Already in that order; nothing to do." })
        }

        const after = db.updateEntity(before, "site", siteId, { contentIds: next })
        commitTracked({
          prevStore: before, nextStore: after, action: "update",
          entityKind: "site", entityId: siteId,
          summary: "Reordered page content blocks",
        })
        return ok({ siteId, order: next })
      }),
    }),

    siteconfig_header_menu_add: tool({
      description: "Adds an item to a site config's header nav (headerMenu), which is a recursive tree — a 'menu' item is a megamenu dropdown, 'link' is a plain nav link, and 'services' is the special Service Map entry. Pass parentItemId to nest under an existing 'menu' item's children, or omit it to add at the top level.",
      parameters: z.object({
        siteId: z.string(),
        parentItemId: z.string().nullable().describe("Id of an existing 'menu' item to nest this under, or null for top-level"),
        type: z.enum(["link", "menu", "services"]),
        title: z.string().describe("Locale -> title text, as a JSON object string, e.g. {\"en-US\": \"About\"}"),
        link: z.string().nullable().describe("URL, for type 'link' only"),
        megamenu: z.boolean().nullable().describe("For type 'menu': true renders a dropdown of its children"),
      }),
      execute: async ({ siteId, parentItemId, type, title: titleJson, link, megamenu }) => tryDo(() => {
        const s0 = ctx.getStore()
        const sc = s0.sites[siteId]
        if (!sc) return fail(`No such site: ${siteId}`)

        const title = parseLocaleMap(titleJson, "title")
        const newItem = { id: ulid(), type, title, children: [] as any[], ...usedFields({ link, megamenu }) }
        const nextMenu = db.cloneMenu(sc.headerMenu ?? [])
        if (parentItemId) {
          const parent = db.findMenuItem(nextMenu, parentItemId)
          if (!parent) return fail(`No such header menu item: ${parentItemId}`)
          if (parent.type !== "menu") return fail(`Item ${parentItemId} is type '${parent.type}', not 'menu' — only a 'menu' item can have children.`)
          parent.children.push(newItem)
        } else {
          nextMenu.push(newItem)
        }

        const after = db.updateEntity(s0, "site", siteId, { headerMenu: nextMenu })
        commitTracked({ prevStore: s0, nextStore: after, action: "update", entityKind: "site", entityId: siteId, summary: "Added header nav item" })
        return ok({ siteId, itemId: newItem.id })
      }),
    }),

    siteconfig_header_menu_update: tool({
      description: "Updates one existing header nav item in place — its type, link, megamenu flag, and/or the title for ONE locale. Use this to translate an existing nav item (pass locale + title) rather than re-creating it. Does not move the item in the tree.",
      parameters: z.object({
        siteId: z.string(),
        itemId: z.string(),
        locale: z.string().nullable().describe("Locale whose title to set; required if title is given, ignored otherwise"),
        title: z.string().nullable().describe("New title text for `locale`. Only that locale's entry changes; other locales are left alone."),
        type: z.enum(["link", "menu", "services"]).nullable(),
        link: z.string().nullable().describe("URL, for type 'link'"),
        megamenu: z.boolean().nullable(),
      }),
      execute: async ({ siteId, itemId, locale, title, type, link, megamenu }) => tryDo(() => {
        const before = ctx.getStore()
        const sc = before.sites[siteId]
        if (!sc) return fail(`No such site: ${siteId}`)
        if (title !== null && !locale) return fail("Pass `locale` alongside `title` so the text lands on one locale.")

        const nextMenu = db.cloneMenu(sc.headerMenu ?? [])
        const item = db.findMenuItem(nextMenu, itemId)
        if (!item) return fail(`No such header menu item: ${itemId}`)

        // Changing a 'menu' to something else would strand its children, which
        // only a 'menu' can hold. Make the caller deal with them explicitly.
        if (type && type !== "menu" && item.type === "menu" && item.children.length) {
          return fail(
            `Item ${itemId} is a menu with ${item.children.length} child item(s); only a 'menu' can have children. ` +
            `Remove or re-parent them first, then change its type.`
          )
        }

        const changed: string[] = []
        if (title !== null && locale) { item.title = { ...item.title, [locale]: title }; changed.push(`title (${locale})`) }
        if (type !== null) { item.type = type; changed.push("type") }
        if (link !== null) { item.link = link; changed.push("link") }
        if (megamenu !== null) { item.megamenu = megamenu; changed.push("megamenu") }
        if (!changed.length) return fail("Nothing to change — supply at least one non-null field.")

        const after = db.updateEntity(before, "site", siteId, { headerMenu: nextMenu })
        commitTracked({
          prevStore: before, nextStore: after, action: title !== null ? "translate" : "update",
          entityKind: "site", entityId: siteId, locale: locale ?? undefined,
          summary: `Updated header nav item: ${changed.join(", ")}`,
        })
        return ok({ siteId, itemId, updated: changed })
      }),
    }),

    siteconfig_header_menu_remove: tool({
      description: "Removes one item (and its children, if any) from a site config's header nav.",
      parameters: z.object({ siteId: z.string(), itemId: z.string() }),
      execute: async ({ siteId, itemId }) => {
        if (!ctx.getStore().sites[siteId]) return fail(`No such site: ${siteId}`)
        const approved = await gate(`Remove header nav item ${itemId}`)
        if (!approved) return fail("Rejected by reviewer.")
        return tryDo(() => {
          // Re-read AFTER the approval await: the HITL queue serializes
          // prompts, so another tool call can have committed while this one
          // was waiting. Writing from a pre-await snapshot would discard it.
          const before = ctx.getStore()
          const sc = before.sites[siteId]
          if (!sc) return fail(`No such site: ${siteId}`)
          const nextMenu = db.removeMenuItem(db.cloneMenu(sc.headerMenu ?? []), itemId)
          const after = db.updateEntity(before, "site", siteId, { headerMenu: nextMenu })
          commitTracked({ prevStore: before, nextStore: after, action: "update", entityKind: "site", entityId: siteId, summary: "Removed header nav item" })
          return ok({ siteId, itemId })
        })
      },
    }),

    siteconfig_footer_update: tool({
      description: "Updates a site config's footer — background/text color and the translated footer text. Does not touch footerLinks; use siteconfig_footer_add_link / siteconfig_footer_remove_link for those.",
      parameters: z.object({
        siteId: z.string(),
        bgColor: z.string().nullable(),
        textColor: z.string().nullable(),
        text: z.string().nullable().describe("Locale -> footer text, as a JSON object string, e.g. {\"en-US\": \"...\"}. Replaces the whole map."),
      }),
      execute: async ({ siteId, bgColor, textColor, text: textJson }) => tryDo(() => {
        const s0 = ctx.getStore()
        const sc = s0.sites[siteId]
        if (!sc) return fail(`No such site: ${siteId}`)
        const patch = usedFields({ bgColor, textColor, text: textJson ? parseLocaleMap(textJson, "text") : null })
        if (!Object.keys(patch).length) return fail("Nothing to change.")
        const nextFooter = { ...(sc.footer ?? { footerLinks: [] }), ...patch }
        const after = db.updateEntity(s0, "site", siteId, { footer: nextFooter })
        commitTracked({ prevStore: s0, nextStore: after, action: "update", entityKind: "site", entityId: siteId, summary: "Updated footer" })
        return ok({ siteId, updated: Object.keys(patch) })
      }),
    }),

    siteconfig_footer_add_link: tool({
      description: "Adds one link to a site config's footer.",
      parameters: z.object({
        siteId: z.string(),
        title: z.string().describe("Locale -> title text, as a JSON object string, e.g. {\"en-US\": \"Privacy Policy\"}"),
        url: z.string().nullable(),
      }),
      execute: async ({ siteId, title: titleJson, url }) => tryDo(() => {
        const s0 = ctx.getStore()
        const sc = s0.sites[siteId]
        if (!sc) return fail(`No such site: ${siteId}`)
        const title = parseLocaleMap(titleJson, "title")
        const footer = sc.footer ?? { footerLinks: [] }
        const nextFooter = { ...footer, footerLinks: [...(footer.footerLinks ?? []), usedFields({ title, url })] }
        const after = db.updateEntity(s0, "site", siteId, { footer: nextFooter })
        commitTracked({ prevStore: s0, nextStore: after, action: "update", entityKind: "site", entityId: siteId, summary: "Added footer link" })
        return ok({ siteId, footerLinkCount: nextFooter.footerLinks.length })
      }),
    }),

    siteconfig_footer_update_link: tool({
      description: "Updates one existing footer link in place, by its 0-based index — its url and/or its title for ONE locale. Use this to translate an existing footer link rather than adding a duplicate.",
      parameters: z.object({
        siteId: z.string(),
        index: z.number().describe("0-based position in footerLinks"),
        locale: z.string().nullable().describe("Locale whose title to set; required if title is given"),
        title: z.string().nullable().describe("New title text for `locale`. Other locales are left alone."),
        url: z.string().nullable(),
      }),
      execute: async ({ siteId, index, locale, title, url }) => tryDo(() => {
        const before = ctx.getStore()
        const sc = before.sites[siteId]
        if (!sc) return fail(`No such site: ${siteId}`)
        if (title !== null && !locale) return fail("Pass `locale` alongside `title` so the text lands on one locale.")

        const links = sc.footer?.footerLinks ?? []
        if (index < 0 || index >= links.length) return fail(`No footer link at index ${index} (there are ${links.length}).`)

        const changed: string[] = []
        const nextLinks = links.map((l, i) => {
          if (i !== index) return l
          const next = { ...l }
          if (title !== null && locale) { next.title = { ...l.title, [locale]: title }; changed.push(`title (${locale})`) }
          if (url !== null) { next.url = url; changed.push("url") }
          return next
        })
        if (!changed.length) return fail("Nothing to change — supply a title (with locale) or a url.")

        const nextFooter = { ...(sc.footer ?? { footerLinks: [] }), footerLinks: nextLinks }
        const after = db.updateEntity(before, "site", siteId, { footer: nextFooter })
        commitTracked({
          prevStore: before, nextStore: after, action: title !== null ? "translate" : "update",
          entityKind: "site", entityId: siteId, locale: locale ?? undefined,
          summary: `Updated footer link: ${changed.join(", ")}`,
        })
        return ok({ siteId, index, updated: changed })
      }),
    }),

    siteconfig_footer_remove_link: tool({
      description: "Removes one footer link from a site config by its position (0-based index in footerLinks).",
      parameters: z.object({ siteId: z.string(), index: z.number() }),
      execute: async ({ siteId, index }) => {
        const preview = ctx.getStore().sites[siteId]
        if (!preview) return fail(`No such site: ${siteId}`)
        const previewLinks = preview.footer?.footerLinks ?? []
        if (index < 0 || index >= previewLinks.length) {
          return fail(`No footer link at index ${index} (there are ${previewLinks.length}).`)
        }
        const approved = await gate(
          `Remove footer link "${previewLinks[index].title?.[ctx.locale] ?? previewLinks[index].url ?? index}"`
        )
        if (!approved) return fail("Rejected by reviewer.")
        return tryDo(() => {
          // Re-read AFTER the approval await — see siteconfig_header_menu_remove.
          // The index is re-validated too, since the list may have shifted.
          const before = ctx.getStore()
          const sc = before.sites[siteId]
          if (!sc) return fail(`No such site: ${siteId}`)
          const links = sc.footer?.footerLinks ?? []
          if (index < 0 || index >= links.length) {
            return fail(`Footer link at index ${index} no longer exists (there are now ${links.length}).`)
          }
          const nextFooter = { ...sc.footer, footerLinks: links.filter((_, i) => i !== index) }
          const after = db.updateEntity(before, "site", siteId, { footer: nextFooter })
          commitTracked({ prevStore: before, nextStore: after, action: "update", entityKind: "site", entityId: siteId, summary: "Removed footer link" })
          return ok({ siteId, footerLinkCount: nextFooter.footerLinks.length })
        })
      },
    }),

    // ── Update ───────────────────────────────────────────────────────────────
    siteconfig_update: tool({
      description: "Update an entity's own columns. Pass only the fields you want to change. Does NOT touch translations — use siteconfig_set_translation for that.",
      parameters: z.object({
        kind: z.enum(ENTITY_KINDS),
        id: z.string(),
        fields: z.object({
          title: z.string().nullable(),
          name: z.string().nullable().describe("site only — public-facing site name"),
          baseUrl: z.string().nullable().describe("site only — public site base url"),
          defaultLocale: z.string().nullable().describe("site only — fallback locale"),
          locales: z.array(z.string()).nullable().describe("site only — replaces the whole list of locales this site publishes in"),
          logo: z.string().nullable().describe("site only — logo URL. You cannot upload files; use the editor's Upload button and reference the URL it produces."),
          googleTagJson: z.string().nullable().describe("site only — Google Analytics ids as a JSON array string, e.g. '[{\"key\":\"G-ABC123\"}]'. Replaces the whole list."),
          transifexProject: z.string().nullable().describe("site only"),
          googleTranslate: z.boolean().nullable().describe("site only — use Google Translate instead of Transifex"),
          cookieBanner: z.string().nullable().describe("siteConfig only"),
          headerBgColor: z.string().nullable(), headerColor: z.string().nullable(),
          headerColorBg: z.string().nullable(), headerColorTxt: z.string().nullable(),
          pageBgColor: z.string().nullable(), pageColor: z.string().nullable(),
          pageColorBg: z.string().nullable(), pageColorTxt: z.string().nullable(),
          ...PAGE_CONTENT_FIELDS,
        }).describe("Columns to change. Send null for anything you are not changing."),
      }),
      execute: async ({ kind, id, fields: rawFields }) => {
        const s0 = ctx.getStore()
        const existing = db.getEntity(s0, kind, id)
        if (!existing) return fail(`No such ${kind}: ${id}`)
        // Merge flags against what is stored, or setting one clears the rest.
        const fields = parseJsonObjectFields(usedFields(rawFields), existing)
        // googleTag is an array of objects, which cannot be expressed in a
        // strict function schema (see the z.record note at the top of this
        // file), so it arrives as a JSON string and is parsed here.
        if (typeof fields.googleTagJson === "string") {
          let parsed: unknown
          try {
            parsed = JSON.parse(fields.googleTagJson)
          } catch {
            return fail("googleTagJson is not valid JSON.")
          }
          if (!Array.isArray(parsed) || parsed.some(t => !t || typeof (t as any).key !== "string")) {
            return fail(`googleTagJson must be a JSON array of {"key": "G-..."} objects.`)
          }
          delete fields.googleTagJson
          fields.googleTag = parsed
        }
        if (!Object.keys(fields).length) return fail("Nothing to change — supply at least one non-null field.")
        if (kind !== "site") {
          const misdirected = Object.keys(fields).filter(f => SITE_ONLY_FIELDS.has(f))
          if (misdirected.length) {
            return fail(`${misdirected.join(", ")} exist only on a site, not on a ${kind}.`)
          }
        }
        const approved = await gate(
          `Update ${kind} "${name(kind, id)}"`,
          Object.entries(fields).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join("\n"),
        )
        if (!approved) return fail("Rejected by reviewer.")
        return tryDo(() => {
          const before = ctx.getStore()
          const after = db.updateEntity(before, kind, id, fields)
          commitTracked({ prevStore: before, nextStore: after, action: "update", entityKind: kind, entityId: id, summary: `Updated ${Object.keys(fields).join(", ")} on ${kind}` })
          ctx.onEntityFocused?.(kind, id)
          return ok({ kind, id, updated: Object.keys(fields) })
        })
      },
    }),

    siteconfig_set_translation: tool({
      description: "Write one locale's translated fields for a pageContent block. siteConfig, headerMenu items, and footer carry their locale text inline instead (edit those with siteconfig_update / siteconfig_header_menu_add / siteconfig_footer_update / siteconfig_footer_add_link). Preserve HTML structure in richtext fields — translate only the human-readable text.",
      parameters: z.object({
        kind: z.literal("pageContent"),
        id: z.string(),
        locale: z.string(),
        fields: z.object(TRANSLATABLE_PAGE_CONTENT_FIELDS).describe("Translated values for this locale. Only genuinely translatable fields are accepted — colours, images, coordinates, links and numbers are locale-independent and are set with siteconfig_update instead. Send null for anything not changing."),
      }),
      execute: async ({ kind, id, locale, fields: rawFields }) => {
        const fields = usedFields(rawFields)
        if (!Object.keys(fields).length) return fail("Nothing to write — provide at least one field.")
        const s0 = ctx.getStore()
        if (!db.getEntity(s0, kind, id)) return fail(`No such ${kind}: ${id}`)
        return tryDo(() => {
          const after = db.setTranslation(s0, kind, id, locale, fields)
          commitTracked({ prevStore: s0, nextStore: after, action: "translate", entityKind: kind, entityId: id, locale, summary: `Translated ${kind} (${locale})` })
          ctx.onEntityFocused?.(kind, id)
          return ok({ kind, id, locale, written: Object.keys(fields) })
        })
      },
    }),

    // ── Delete ───────────────────────────────────────────────────────────────
    siteconfig_delete: tool({
      description: "Delete an entity (siteConfig or pageContent). Deleting a pageContent detaches it from any siteConfig that listed it in contentIds (id pruned, not cascaded). Deleting a siteConfig removes its embedded headerMenu/footer along with it — those aren't separate entities.",
      parameters: z.object({ kind: z.enum(ENTITY_KINDS), id: z.string() }),
      execute: async ({ kind, id }) => {
        const s0 = ctx.getStore()
        if (!db.getEntity(s0, kind, id)) return fail(`No such ${kind}: ${id}`)
        const refs = db.findReferences(s0, kind, id)
        const label = `${kind} "${name(kind, id)}"`
        const approved = await gate(
          `Delete ${label}`,
          refs.length
            ? `This will detach ${refs.length} reference(s):\n` + refs.slice(0, 20).map(r => `- ${r.kind}.${r.field}`).join("\n")
            : "Nothing references this record.",
        )
        if (!approved) return fail("Rejected by reviewer.")
        return tryDo(() => {
          const before = ctx.getStore()
          const { store, detached } = db.deleteEntity(before, kind, id)
          commitTrackedDelete({ prevStore: before, nextStore: store, entityKind: kind, entityId: id, summary: `Deleted ${label}` })
          ctx.onEntityDeleted?.(kind, id)
          return ok({ kind, id, detached })
        })
      },
    }),

    // ── History ──────────────────────────────────────────────────────────────
    siteconfig_history: tool({
      description: "List recent logged changes, newest last. Use to find an entry id for siteconfig_revert, or to review what has changed recently.",
      parameters: z.object({ limit: z.number().nullable() }),
      execute: async ({ limit }) => tryDo(() => {
        const entries = ctx.getHistory?.() ?? []
        const slice = entries.slice(-(limit ?? 50))
        return ok({
          total: entries.length,
          entries: slice.map(e => ({
            id: e.id, at: new Date(e.at).toISOString(), action: e.action, actor: e.actor,
            entityKind: e.entityKind, entityId: e.entityId, entityName: e.entityName,
            summary: e.summary, reverted: !!e.reverted,
          })),
        })
      }),
    }),

    siteconfig_entity_versions: tool({
      description: "The version timeline for one entity, newest first. Use before siteconfig_restore_version to see what's available.",
      parameters: z.object({ entityId: z.string() }),
      execute: async ({ entityId }) => tryDo(() => {
        const versions = ctx.versionsOf?.(entityId) ?? []
        return ok({
          entityId,
          versions: versions.map(v => ({
            entryId: v.entryId, at: new Date(v.at).toISOString(), action: v.action,
            author: v.author, actor: v.actor, summary: v.summary,
            restorable: !!v.entity, isPriorState: !!v.isPriorState,
          })),
        })
      }),
    }),

    siteconfig_restore_version: tool({
      description: "Restores an entity to a previously-recorded version. Logged as an update, so the restore itself is revertable. Always tell the user what you are restoring before calling this.",
      parameters: z.object({ kind: z.enum(ENTITY_KINDS), entityId: z.string(), entryId: z.string() }),
      execute: async ({ kind, entityId, entryId }) => {
        if (!ctx.restoreEntityVersion) return fail("Version restore is unavailable.")
        const approved = await gate(`Restore ${kind} "${name(kind, entityId)}" to an earlier version`)
        if (!approved) return fail("Rejected by reviewer.")
        const result = ctx.restoreEntityVersion(entityId, entryId, kind)
        if (result.ok === false) return fail(result.error)
        ctx.onEntityFocused?.(kind, entityId)
        return ok({ entityId, description: result.description })
      },
    }),

    siteconfig_revert_change: tool({
      description: "Undo one logged change by its id, restoring the entity to how it was immediately before. Find ids with siteconfig_history.",
      parameters: z.object({ entryId: z.string() }),
      execute: async ({ entryId }) => {
        const entries = ctx.getHistory?.() ?? []
        const entry = entries.find(e => e.id === entryId)
        if (!entry) return fail(`No history entry with id ${entryId}`)
        if (!ctx.revert) return fail("Revert is unavailable.")
        const approved = await gate(`Undo: ${entry.summary}`)
        if (!approved) return fail("Rejected by reviewer.")
        const result = ctx.revert(entryId)
        if (result.ok === false) return fail(result.error)
        if (entry.entityKind) ctx.onEntityFocused?.(entry.entityKind, entry.entityId!)
        return ok({ entryId, reverted: true, description: result.description })
      },
    }),
  }
}

export const SITECONFIG_TOOL_LABELS: Record<string, string> = {
  siteconfig_overview: "Site Config Overview",
  siteconfig_search: "Search Site Config",
  siteconfig_list: "List Entities",
  siteconfig_get: "Get Entity",
  siteconfig_pagecontent_field_guide: "Page Content Field Guide",
  siteconfig_create_site: "Create Site",
  siteconfig_set_service_map_scope: "Set Service Map Scope",
  siteconfig_set_articles_scope: "Set Articles Scope",
  siteconfig_create_pagecontent: "Create Page Content",
  siteconfig_attach_pagecontent: "Attach Page Content",
  siteconfig_reorder_content: "Reorder Page Content",
  siteconfig_header_menu_add: "Add Header Nav Item",
  siteconfig_header_menu_update: "Update Header Nav Item",
  siteconfig_header_menu_remove: "Remove Header Nav Item",
  siteconfig_footer_update: "Update Footer",
  siteconfig_footer_add_link: "Add Footer Link",
  siteconfig_footer_update_link: "Update Footer Link",
  siteconfig_footer_remove_link: "Remove Footer Link",
  siteconfig_update: "Update Entity",
  siteconfig_set_translation: "Set Translation",
  siteconfig_delete: "Delete Entity",
  siteconfig_history: "History",
  siteconfig_entity_versions: "Entity Versions",
  siteconfig_restore_version: "Restore Version",
  siteconfig_revert_change: "Revert Change",
}
